import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Check,
  Loader2,
  Package as PackageIcon,
  Zap,
  AlertCircle,
  Bitcoin,
  Key,
  Globe,
  Users,
  Sparkles,
  FileText,
  Plus,
  X,
  ShoppingCart,
  Wallet,
  Coins,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  usePackages,
  useActiveAddons,
  type Package,
  type ActiveAddon,
} from '@/hooks/useBilling';
import { useActiveWallets, type PublicWallet } from '@/hooks/useCrypto';
import { useCart, useCartCheckoutCrypto } from '@/hooks/useCart';
import { ApiError } from '@/lib/api-client';
import { cn, formatDate } from '@/lib/utils';

const ADDON_ICONS: Record<string, typeof Sparkles> = {
  api_access: Zap,
  byok: Key,
  long_articles: FileText,
  web_grounding: Sparkles,
  extra_site: Globe,
  extra_editor: Users,
};

const ADDON_LABEL: Record<string, string> = {
  api_access: 'API Erişimi',
  byok: 'BYOK (Kendi AI Key)',
  long_articles: 'Uzun Makale',
  web_grounding: 'Güncel Bilgi (Web)',
  extra_site: '+1 Site',
  extra_editor: '+1 Editör',
};

const BALANCE_PRESETS_CENTS = [500, 1000, 2000, 5000, 10000];

function formatPrice(cents: number | null): string {
  if (cents == null) return '—';
  if (cents === 0) return 'Ücretsiz';
  return `$${(cents / 100).toFixed(2)}`;
}

// Only extra_site / extra_editor benefit from repeat purchases — buying
// two copies actually adds two slots. Binary feature flags flip on with
// one grant, so we block duplicate copies in the cart UI too.
const STACKABLE_KINDS = new Set(['balance', 'extra_site', 'extra_editor']);

export function PackagesPage() {
  const { data, isPending } = usePackages();
  const addons = useActiveAddons();
  const cart = useCart();

  const balanceCustomPkg = (data?.packages ?? []).find((p) => p.id === 'balance-custom');
  const addonPacks = (data?.packages ?? []).filter((p) => p.kind !== 'balance');
  const pkgById = useMemo(() => {
    const m = new Map<string, Package>();
    for (const p of data?.packages ?? []) m.set(p.id, p);
    return m;
  }, [data]);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
      <div className="space-y-8 min-w-0">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <PackageIcon className="w-5 h-5" /> Ödeme & Eklentiler
          </h1>
          <p className="text-muted-foreground text-sm">
            Sepete ekle, tek seferde öde. Aynı eklentiyi birden fazla alıp sürelerini topla.
          </p>
        </div>

        {(addons.data?.addons.length ?? 0) > 0 && (
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">Aktif Eklentilerin</h2>
            <div className="grid gap-2 md:grid-cols-2">
              {groupAddons(addons.data!.addons).map((g) => (
                <ActiveAddonCard key={g.key} group={g} />
              ))}
            </div>
          </section>
        )}

        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold">Bakiye Ekle</h2>
            <p className="text-xs text-muted-foreground">
              İstediğin kadar USD bakiye ekle. Bakiyen bitene kadar süresiz kullanılır.
            </p>
          </div>
          {isPending ? (
            <div className="h-48 rounded-lg border bg-muted/30 animate-pulse" />
          ) : !balanceCustomPkg ? (
            <p className="text-sm text-muted-foreground">
              Bakiye ürünü tanımlı değil. Platform yöneticisi DB'ye `balance-custom` eklemeli.
            </p>
          ) : (
            <BalanceTopupCard onAdd={(cents) => cart.add('balance-custom', cents)} />
          )}
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold">Özellik Eklentileri</h2>
            <p className="text-xs text-muted-foreground">
              Her özellik ayrı alınır, kendi süresi vardır. +1 Site ve +1 Editör üstüste alınabilir.
            </p>
          </div>
          {addonPacks.length === 0 ? (
            <p className="text-sm text-muted-foreground">Eklenti tanımlı değil.</p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {addonPacks.map((p) => (
                <AddonRow key={p.id} pkg={p} count={cart.count(p.id)} onAdd={() => cart.add(p.id)} />
              ))}
            </div>
          )}
        </section>

        <div className="text-center text-sm text-muted-foreground">
          <Link to="/account/orders" className="text-primary hover:underline">
            Satın alma geçmişim →
          </Link>
        </div>
      </div>

      {/* Sticky cart panel */}
      <div>
        <div className="lg:sticky lg:top-20">
          <CartPanel cart={cart} pkgById={pkgById} />
        </div>
      </div>
    </div>
  );
}

// Same-cart purchases share paid_at → identical expires_at, so grouping
// by (addon_type, package_id, expires_at) merges "20× +1 Site bought
// together" into one card while keeping separate purchases (different
// expiry) as their own cards.
type AddonGroup = {
  key: string;
  addon_type: string;
  package_id: string | null;
  expires_at: number | null;
  qty: number;
};

function groupAddons(addons: ActiveAddon[]): AddonGroup[] {
  const map = new Map<string, AddonGroup>();
  for (const a of addons) {
    const k = `${a.addon_type}|${a.package_id ?? ''}|${a.expires_at ?? ''}`;
    const g = map.get(k);
    if (g) {
      g.qty++;
    } else {
      map.set(k, {
        key: k,
        addon_type: a.addon_type,
        package_id: a.package_id,
        expires_at: a.expires_at,
        qty: 1,
      });
    }
  }
  return Array.from(map.values());
}

function ActiveAddonCard({ group }: { group: AddonGroup }) {
  const Icon = ADDON_ICONS[group.addon_type] ?? Sparkles;
  const label = ADDON_LABEL[group.addon_type] ?? group.addon_type;
  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardContent className="p-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-md bg-primary/15 text-primary flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">
            {label}
            {group.qty > 1 && (
              <span className="ml-1.5 text-xs font-normal text-muted-foreground">× {group.qty}</span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {group.expires_at ? `Bitiş: ${formatDate(group.expires_at)}` : 'Süresiz'}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BalanceTopupCard({ onAdd }: { onAdd: (cents: number) => void }) {
  const [customUsd, setCustomUsd] = useState('');

  const onAddCustom = () => {
    const usd = parseFloat(customUsd);
    if (!Number.isFinite(usd) || usd < 1) return;
    const cents = Math.round(usd * 100);
    onAdd(cents);
    setCustomUsd('');
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Coins className="w-4 h-4 text-primary" /> Bakiye Yükle
        </CardTitle>
        <CardDescription>
          Aşağıdaki tutarlardan seç veya dilediğin USD miktarını yaz (min $1).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-5 gap-2">
          {BALANCE_PRESETS_CENTS.map((cents) => (
            <Button
              key={cents}
              variant="outline"
              size="sm"
              onClick={() => onAdd(cents)}
              className="flex-col h-auto py-2"
            >
              <span className="font-semibold text-sm">${cents / 100}</span>
              <span className="text-[10px] text-muted-foreground">ekle</span>
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2 pt-2 border-t">
          <div className="flex items-center gap-1 flex-1">
            <span className="text-sm font-mono text-muted-foreground">$</span>
            <Input
              type="number"
              min={1}
              step="0.01"
              placeholder="özel tutar"
              value={customUsd}
              onChange={(e) => setCustomUsd(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onAddCustom();
                }
              }}
              className="h-9 font-mono"
            />
          </div>
          <Button size="sm" onClick={onAddCustom} disabled={!customUsd || parseFloat(customUsd) < 1}>
            <Plus className="w-3.5 h-3.5" /> Sepete Ekle
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AddonRow({ pkg, count, onAdd }: { pkg: Package; count: number; onAdd: () => void }) {
  const Icon = ADDON_ICONS[pkg.kind] ?? Sparkles;
  const stackable = STACKABLE_KINDS.has(pkg.kind);
  const locked = count > 0 && !stackable;
  return (
    <Card className={cn(count > 0 && 'ring-1 ring-primary')}>
      <CardHeader className="pb-2">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1 space-y-0.5">
            <CardTitle className="text-sm">{pkg.name}</CardTitle>
            {pkg.description && (
              <CardDescription className="text-xs">{pkg.description}</CardDescription>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-baseline justify-between">
          <div className="text-xl font-semibold">{formatPrice(pkg.price_usd_cents)}</div>
          <div className="text-xs text-muted-foreground">{pkg.duration_days} gün</div>
        </div>
        <Button
          size="sm"
          variant={count > 0 ? 'secondary' : 'default'}
          className="w-full"
          onClick={onAdd}
          disabled={locked}
        >
          <Plus className="w-3.5 h-3.5" />
          {locked
            ? 'Sepette — sadece 1 adet'
            : count > 0
            ? `Sepette ${count} — Tekrar ekle`
            : 'Sepete Ekle'}
        </Button>
      </CardContent>
    </Card>
  );
}

type GroupedLine = {
  package_id: string;
  amount_cents?: number;
  qty: number;
  keys: string[];
  pkg?: Package;
  /** Per-unit cents (pkg.price_usd_cents for normal, custom for balance-custom) */
  unit_cents: number;
};

function CartPanel({ cart, pkgById }: { cart: ReturnType<typeof useCart>; pkgById: Map<string, Package> }) {
  const wallets = useActiveWallets();
  const checkout = useCartCheckoutCrypto();
  const navigate = useNavigate();
  const [selectedWallet, setSelectedWallet] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Group by (package_id, amount_cents). Different balance amounts stay
  // as separate rows so the user sees "Bakiye $5 × 3" and "Bakiye $10 × 1"
  // as distinct lines.
  const grouped = useMemo<GroupedLine[]>(() => {
    const map = new Map<string, GroupedLine>();
    for (const it of cart.items) {
      const pkg = pkgById.get(it.package_id);
      const unitCents = it.amount_cents ?? pkg?.price_usd_cents ?? 0;
      const k = `${it.package_id}|${it.amount_cents ?? ''}`;
      const row = map.get(k) ?? {
        package_id: it.package_id,
        amount_cents: it.amount_cents,
        qty: 0,
        keys: [],
        pkg,
        unit_cents: unitCents,
      };
      row.qty++;
      row.keys.push(it.key);
      map.set(k, row);
    }
    return Array.from(map.values());
  }, [cart.items, pkgById]);

  const totalCents = grouped.reduce((s, l) => s + l.unit_cents * l.qty, 0);
  const maxDurationDays = grouped.reduce(
    (m, l) => (l.pkg && l.pkg.kind === 'balance' ? m : Math.max(m, l.pkg?.duration_days ?? 0)),
    0,
  );

  const walletList = wallets.data?.wallets ?? [];
  const isEmpty = cart.items.length === 0;

  const onCheckout = async () => {
    setError(null);
    if (!selectedWallet) {
      setError('Ödeme yapmak için bir ağ seç.');
      return;
    }
    try {
      const items = cart.items.map((i) =>
        i.amount_cents != null
          ? { package_id: i.package_id, amount_cents: i.amount_cents }
          : { package_id: i.package_id },
      );
      const res = await checkout.mutateAsync({ items, wallet_id: selectedWallet });
      cart.clear();
      navigate({ to: '/checkout/cart/$cartId', params: { cartId: res.cart_id } });
    } catch (err) {
      if (err instanceof ApiError) {
        const issues = (err.details as Array<{ path?: unknown[]; message?: string }> | undefined) ?? [];
        const extra = issues.length
          ? ' — ' + issues.map((i) => `${(i.path ?? []).join('.')}: ${i.message ?? ''}`).join('; ')
          : '';
        setError(err.message + extra);
      } else {
        setError('Sepet oluşturulamadı');
      }
    }
  };

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ShoppingCart className="w-4 h-4" /> Sepet
          {cart.items.length > 0 && (
            <span className="ml-auto text-xs font-normal text-muted-foreground">
              {cart.items.length} kalem
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isEmpty ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            Bir ürün seç, burada görünsün.
          </p>
        ) : (
          <>
            <ul className="space-y-1.5 text-sm max-h-72 overflow-y-auto pr-1">
              {grouped.map((l) => {
                const lineTotal = l.unit_cents * l.qty;
                const stackable = l.pkg ? STACKABLE_KINDS.has(l.pkg.kind) : true;
                const isCustomBalance = l.package_id === 'balance-custom';
                const displayName = isCustomBalance
                  ? `Bakiye $${(l.unit_cents / 100).toFixed(2)}`
                  : l.pkg?.name ?? l.package_id;
                return (
                  <li
                    key={`${l.package_id}|${l.amount_cents ?? ''}`}
                    className="flex items-center gap-2 rounded-md bg-muted/40 px-2 py-1.5"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-medium">{displayName}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {formatPrice(l.unit_cents)}
                        {l.pkg && l.pkg.kind !== 'balance' && ` · ${l.pkg.duration_days} gün`}
                        {stackable && l.qty > 1 && (
                          <>
                            {' · toplam '}
                            <span className="font-mono">{formatPrice(lineTotal)}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {stackable ? (
                        <>
                          <button
                            type="button"
                            onClick={() => cart.remove(l.keys[l.keys.length - 1])}
                            className="w-6 h-6 rounded border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background"
                            title="Bir azalt"
                          >
                            −
                          </button>
                          <span className="text-xs font-medium w-6 text-center">{l.qty}</span>
                          <button
                            type="button"
                            onClick={() => cart.add(l.package_id, l.amount_cents)}
                            className="w-6 h-6 rounded border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background"
                            title="Bir arttır"
                          >
                            +
                          </button>
                        </>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => cart.removeAll(l.package_id, l.amount_cents)}
                        className="ml-1 text-muted-foreground hover:text-destructive"
                        title="Satırı kaldır"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="border-t pt-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Toplam</span>
                <span className="font-semibold">{formatPrice(totalCents)}</span>
              </div>
              {maxDurationDays > 0 && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Eklenti süresi (en uzun)</span>
                  <span>{maxDurationDays} gün</span>
                </div>
              )}
            </div>

            <div className="space-y-2 border-t pt-3">
              <div className="text-xs font-medium text-muted-foreground">
                <Wallet className="w-3 h-3 inline mr-1" /> Ödeme ağı
              </div>
              {walletList.length === 0 ? (
                <p className="text-xs text-muted-foreground">Aktif cüzdan yok.</p>
              ) : (
                <div className="space-y-1">
                  {walletList.map((w) => (
                    <WalletOption
                      key={w.id}
                      wallet={w}
                      selected={selectedWallet === w.id}
                      onSelect={() => setSelectedWallet(w.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div className="text-xs text-destructive bg-destructive/10 rounded px-2 py-1.5 flex items-start gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Button
                className="w-full"
                onClick={onCheckout}
                disabled={checkout.isPending || !selectedWallet}
              >
                {checkout.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Bitcoin className="w-4 h-4" />
                )}
                Ödemeye Geç
              </Button>
              <Button variant="ghost" size="sm" className="w-full" onClick={cart.clear}>
                Sepeti Temizle
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function WalletOption({
  wallet,
  selected,
  onSelect,
}: {
  wallet: PublicWallet;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full flex items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-xs transition-colors',
        selected ? 'border-primary bg-primary/5 text-foreground' : 'border-border hover:bg-accent',
      )}
    >
      <span className="flex items-center gap-2">
        <Check className={cn('w-3 h-3', selected ? 'text-primary' : 'opacity-0')} />
        <span className="font-medium">{wallet.asset}</span>
        <span className="text-muted-foreground">{wallet.network.toUpperCase()}</span>
      </span>
    </button>
  );
}
